import { AdminOptionsLang } from "../../resources/AdminOptionsLang";
import { ContactPointLang } from "../../resources/ContactPointLang";
import { ErrorsLang } from "../../resources/ErrorsLang";
import { IndividualPrefixLang } from "../../resources/IndividualPrefixLang";
import { LabelsLang } from "../../resources/LabelsLang";
import { CountriesLang } from "../../resources/CountriesLang";
import { MessagesAndInstructionsLang } from "../../resources/MessagesAndInstructionsLang";
import { PhoneTypeLang } from "../../resources/PhoneTypeLang";
import { TitlesLang } from "../../resources/TitlesLang";
import { ValidationsLang } from "../../resources/ValidationsLang";
import { SexAtBirthLang } from "../../resources/SexAtBirthLang";
import {
    IGenericsLang,
    CountryStrings,
    ILabelSelect,
    ILanguageLocaleService,
    IGeneralCountryMappedLang,
    IPatientOverviewLang
} from "./Types";
import { LanguageResourceDto } from "../../dtos/KioskDtos.dtos";
import { CountryRegions } from "../../resources/CountryRegions";
import { LanguageLang } from "../../resources/LanguageLang";
import { LanguageCodes } from "../../constants/languages";
import { isNullOrUndefined } from "util";
import { PreferredPronounsLang } from "../../resources/PreferredPronounsLang";
import { GenderIdentityLang } from "../../resources/GenderIdentityLang";

export class LanguageResource implements ILanguageLocaleService {
    private genericStrings: IGenericsLang = {} as IGenericsLang;
    private adminOptions = new AdminOptionsLang();
    private contactPoint = new ContactPointLang();
    private errors = new ErrorsLang();
    private individualPrefix = new IndividualPrefixLang();
    private labels = new LabelsLang();
    private countries = new CountriesLang();
    private countryRegions = new CountryRegions();
    private messagesAndInstructions = new MessagesAndInstructionsLang();
    private phoneType = new PhoneTypeLang();
    private titles = new TitlesLang();
    private validation = new ValidationsLang();
    private sexAtBirth = new SexAtBirthLang();
    private genderIdentity = new GenderIdentityLang();
    private languages = new LanguageLang();
    private preferredPronouns = new PreferredPronounsLang();

    constructor(langRessource: LanguageResourceDto) {
        this.adminOptions = Object.assign(new AdminOptionsLang(), langRessource.adminOptions);
        this.contactPoint = Object.assign(new ContactPointLang(), langRessource.contactPoint);
        this.errors = Object.assign(new ErrorsLang(), langRessource.errors);
        this.individualPrefix = Object.assign(new IndividualPrefixLang(), langRessource.individualPrefix);
        this.labels = Object.assign(new LabelsLang(), langRessource.labels);
        this.countries = Object.assign(new CountriesLang(), langRessource.countries);
        this.countryRegions = Object.assign(new CountryRegions(), langRessource.regions)
        this.messagesAndInstructions = Object.assign(new MessagesAndInstructionsLang(), langRessource.messagesAndInstructions);
        this.phoneType = Object.assign(new PhoneTypeLang(), langRessource.phoneType);
        this.titles = Object.assign(new TitlesLang(), langRessource.titles);
        this.validation = Object.assign(new ValidationsLang(), langRessource.validation);
        this.sexAtBirth = Object.assign(new SexAtBirthLang(), langRessource.sexAtBirth);
        this.genderIdentity = Object.assign(new GenderIdentityLang(), langRessource.genderIdentity);
        this.languages = Object.assign(new LanguageLang(), { Language: langRessource.languages });
        this.genericStrings = {
            labels: this.labels,
            titles: this.titles,
            validations: this.validation,
            messagesInstructions: this.messagesAndInstructions
        };
        this.preferredPronouns = Object.assign(new PreferredPronounsLang(), langRessource.preferredPronouns);
    }

    public getGenderIdentities(): GenderIdentityLang {
        return this.genderIdentity
    }

    public getPreferredPronouns(): PreferredPronounsLang {
        return this.preferredPronouns
    }

    public getGeneralStrings(): IGenericsLang {
        return this.genericStrings
    }

    public getGeneralCountryMappedStrings(): IGeneralCountryMappedLang {
        return {
            regionLabel: this.getRegionLabelStringsForEachCountry(),
            healthCardIdLabel: this.getHealthCardLabelStringsForEachCountry(),
            regionCodeValidation: this.getRegionCodeValidationForEachCountry(),
            regionCodeLabel: this.getRegionCodeLabelForEachCountry(),
            regionCodeWrongEnteredValidation: this.getRegionCodeWrongEnteredValidationForEachCountry()
        }
    }

    public getRegionCodeLabelForEachCountry() {
        return {
            CA: this.labels.RegionPostalCodeLabel,
            US: this.labels.RegionZipCodeLabel,
            MX: this.labels.RegionZipCodeLabel,
            CO: this.labels.RegionZipCodeLabel
        }
    }
    public getPatientOverviewStrings(): IPatientOverviewLang {
        return {
            individualPrefix: this.individualPrefix,
            phoneType: this.phoneType,
            contactPoint: this.contactPoint,
            sexAtBirth: this.sexAtBirth,
            genderIdentity: this.genderIdentity,
            country: this.countries,
            regions: this.countryRegions,
            notifications: this.labels.NotificationsLabel,
            preferredPronouns: this.preferredPronouns
        }
    }

    public getErrorStrings(): ErrorsLang {
        return this.errors;
    }

    public getAdminOptionString(): AdminOptionsLang {
        return this.adminOptions;
    }

    public getCountries(): CountriesLang {
        return this.countries;
    }

    public getCountryRegionsByCountryCode(country: string): any {
        return this.countryRegions[country];
    }

    public getPrefixStrings(): IndividualPrefixLang {
        return this.individualPrefix
    }

    public getRegionStrings(): CountryRegions {
        return this.countryRegions;
    }

    public getPhoneTypes(): PhoneTypeLang {
        return this.phoneType;
    }

    public getContactPoints(): ContactPointLang {
        return this.contactPoint;
    }

    public getSexAtBirthValues(): SexAtBirthLang {
        return this.sexAtBirth;
    }

    public getAllCountryRegionsOptionsForSpecificCountryAndRegions(options: { [countryCode: string]: string[] }): { [countryCode: string]: ILabelSelect[] } {
        const allRegions: { [index: string]: ILabelSelect[] } = {}

        for (const countryCode of Object.keys(options)) {
            if (isNullOrUndefined(allRegions[countryCode])) {
                allRegions[countryCode] = [];
            }

            if (options[countryCode].any()) {
                const regionVocabs = this.getCountryRegionsByCountryCode(countryCode);
                for (const regionCode of options[countryCode]) {
                    allRegions[countryCode].push({ label: regionVocabs[regionCode], value: regionCode });
                }
            } else {
                allRegions[countryCode] = this.getRegionsByCountrySelectionOptions(countryCode);
            }
        }

        return allRegions;
    }

    public getAllCountryRegionsOptions(): { [countryCode: string]: ILabelSelect[] } {
        return {
            CA: this.getRegionsByCountrySelectionOptions("CA"),
            US: this.getRegionsByCountrySelectionOptions("US"),
            CO: this.getRegionsByCountrySelectionOptions("CO"),
            MX: this.getRegionsByCountrySelectionOptions("MX"),
        }
    }

    public getPrefixSelectionOptions(): ILabelSelect[] {
        return this.createComponentSelectOptions(
            Object.keys(this.getPrefixStrings()),
            this.getPrefixStrings()
        )
    }

    public getCountriesSelectionOptions(countries?: string[]): ILabelSelect[] {
        return this.createComponentSelectOptions(
            countries || Object.keys(this.getCountries()),
            this.getCountries()
        )
    }

    public getRegionsByCountrySelectionOptions(country: string): ILabelSelect[] {
        const regionStrings = this.getCountryRegionsByCountryCode(country);
        return this.createComponentSelectOptions(
            Object.keys(regionStrings),
            regionStrings
        )
    }

    public getPhoneSelectionOptions(): ILabelSelect[] {
        const contactPointString = this.getContactPoints();
        const phoneTypeStrings = this.getPhoneTypes();
        const phoneSelectionOptions: ILabelSelect[] = []
        Object.keys(contactPointString).forEach(contactPointKey => {
            Object.keys(phoneTypeStrings).forEach(phoneTypeKey => {
                phoneSelectionOptions.push({
                    value: contactPointKey + "-" + phoneTypeKey,
                    label: contactPointString[contactPointKey] + " - " + phoneTypeStrings[phoneTypeKey]
                })
            })
        })
        return phoneSelectionOptions;
    }

    public getContactPointSelectionOptions(): ILabelSelect[] {
        return this.createComponentSelectOptions(
            Object.keys(this.getContactPoints()),
            this.getContactPoints()
        )
    }

    public getLanguageLitteralStrings(): LanguageLang {
        return this.languages;
    }

    public getLanguageSelectionOptions(): ILabelSelect[] {
        return Object
            .keys(LanguageCodes)
            .map(key => (
                { label: this.languages.Language[key], value: key }
            ));
    }

    private getRegionLabelStringsForEachCountry(): any {
        return {
            CA: this.genericStrings.labels.RegionProvinceLabel,
            US: this.genericStrings.labels.RegionStateLabel,
            MX: this.genericStrings.labels.RegionStateLabel,
            CO: this.genericStrings.labels.RegionDepartmentLabel
        }
    }

    private getRegionCodeWrongEnteredValidationForEachCountry(): CountryStrings {
        return {
            CA: this.validation.IncorrectPostalCodeValidation,
            US: this.validation.IncorrectZipCodeValidation,
            CO: this.validation.IncorrectZipCodeValidation,
            MX: this.validation.IncorrectZipCodeValidation,
        }
    }

    private getHealthCardLabelStringsForEachCountry(): CountryStrings {
        return {
            CA: this.genericStrings.labels.HealthCardNamLabel,
            US: this.genericStrings.labels.HealthCardUsaLabel,
            MX: this.genericStrings.labels.HealthCardCurpLabel,
            CO: this.genericStrings.labels.HealthCardCurpLabel
        };
    }

    private getRegionCodeValidationForEachCountry(): CountryStrings {
        return {
            CA: this.genericStrings.validations.IncorrectPostalCodeValidation,
            US: this.genericStrings.validations.IncorrectZipCodeValidation,
            MX: this.genericStrings.validations.IncorrectZipCodeValidation,
            CO: this.genericStrings.validations.IncorrectZipCodeValidation
        };
    }

    private createComponentSelectOptions(keys: any[], data: object): ILabelSelect[] {
        const selectionOptions: ILabelSelect[] = [];
        keys.forEach(key => {
            selectionOptions.push({ label: data[key], value: key });
        })
        selectionOptions.sortByProperty(x => x.label);
        return selectionOptions;
    }
}



// WEBPACK FOOTER //
// ./src/services/Language/LanguageResource.ts